import { useState } from 'react';
import { Box, IconButton, Table, TableBody, TableHead, TableRow, Tooltip } from '@mui/material';
import { FilterAltOff, Search } from '@mui/icons-material';
import {
  CustomTextField,
  GeneralTableBox,
  HeadTableCell,
  HorizontalForm,
  NoDataSkeleton,
  SortableHeadTableCell,
} from '../../../common/components';
import { useTableSort } from '../../../common/hooks/useTableSort';
import { useDebouncedValue } from '../../../common/hooks/useDebouncedValue';
import { sortRows } from '../../../utils/sortRows';
import ConsumptionReturnItemRow from './ConsumptionReturnItemRow';
import { RETURN_TEXTS as TEXTS } from '../constants/consumptionReturnTexts';

import ClientPaginatedTable from '../../../common/components/table/ClientPaginatedTable';
/** Cantidades y origen de cada renglón; los filtros conservan lo seleccionado. */
const ConsumptionReturnItemsTable = ({ items, ...actions }) => {
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search).trim().toLowerCase();
  const { sort, toggleSort, resetSort } = useTableSort();
  const rows = sortRows(
    items.filter((row) =>
      `${row.article_name} ${row.article_code} ${row.consumption_number || ''}`
        .toLowerCase()
        .includes(query)
    ),
    sort
  );
  const columns = [
    { key: 'article_name', label: TEXTS.article },
    { key: 'limit', label: TEXTS.limit, width: 120 },
    { key: 'quantity', label: actions.receiving ? TEXTS.accepted : TEXTS.quantity, width: 140 },
  ];
  return (
    <Box>
      <HorizontalForm
        withBorder={false}
        sx={{ px: 2, py: 1.5, gap: 1, alignItems: 'center' }}
      >
        <CustomTextField
          label={TEXTS.searchItems}
          value={search}
          sx={{ flex: '1 1 240px', minWidth: 0, mt: 0 }}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{ startAdornment: <Search sx={{ color: 'primary.main', mr: 1 }} /> }}
        />
        <Tooltip title={TEXTS.clear}>
          <IconButton
            color="primary"
            aria-label={TEXTS.clear}
            onClick={() => {
              setSearch('');
              resetSort();
            }}
            sx={{ flexShrink: 0 }}
          >
            <FilterAltOff />
          </IconButton>
        </Tooltip>
      </HorizontalForm>
      <ClientPaginatedTable rows={rows} resetKey={`${query}:${sort?.key}:${sort?.direction}`}>
        {(pageRows) => (
          <GeneralTableBox fixedLayout minTableWidth={660} hasMaxHeight={false}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <SortableHeadTableCell
                      key={column.key}
                      sortKey={column.key}
                      sort={sort}
                      onSort={toggleSort}
                      sx={{
                        width: column.width,
                        textAlign: column.key === 'article_name' ? 'left' : 'right',
                      }}
                    >
                      {column.label}
                    </SortableHeadTableCell>
                  ))}
                  <HeadTableCell sx={{ width: 70 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((row) => (
                  <ConsumptionReturnItemRow key={row.key} row={row} {...actions} />
                ))}
                {!rows.length && (
                  <NoDataSkeleton
                    colSpan={4}
                    message={query ? TEXTS.noMatchingItems : TEXTS.noItems}
                    showSubtitle={false}
                  />
                )}
              </TableBody>
            </Table>
          </GeneralTableBox>
        )}
      </ClientPaginatedTable>
    </Box>
  );
};

export default ConsumptionReturnItemsTable;
